import { useEffect } from 'react';
import {
  isLiveComposition,
  numberInputBackstop,
  numberKeypressFilter,
} from 'helpers/numberKeypressFilter';

/**
 * The embedded rating dialog's root, and the field selector this hook matches
 * (`component_selector.js:447` verbatim — note `decimal` but not `integer`).
 *
 * Exported because `useBoardNumberInputs` has to skip EXACTLY what this hook
 * handles and nothing more: the two scopes overlap, and legacy's board
 * binding covered the integer-only boxes this selector omits. Two hand-kept
 * copies of one string would drift into the dialog being filtered twice or
 * not at all, and nothing — not tsc, not a test — would say so.
 */
export const RATING_DIALOG_SCOPE = '#component_rating_mode';
export const RATING_FIELD_SELECTOR =
  'input[data-type="number"], input[data-formatter_type="decimal"]';

/**
 * `component_selector.js:33` — the rating dialog's number-input character
 * filter, which the chassis port left out (its §30.6 note reuses formKit's
 * `BareInput`, and that carries no keystroke filtering).
 *
 *   'keypress input[data-type="number"], input[data-formatter_type="decimal"]':
 *     'numberKeypress'
 *
 * The binding lives on the component_selector MainView, whose `el` wraps every
 * per-component selector view, so a keypress in the Gas Heater / Coil / Fan /
 * accessory panes bubbles up to it. `#component_rating_mode` (BoardContent
 * §30.1) is that `el` here, so the `closest()` gate reproduces the delegation
 * scope exactly.
 *
 * ★ This is NOT the only binding over these inputs. Legacy nests
 * `#component_rating_mode` inside `#ahu-board-content` (`main.tmpl:322`), so
 * the AHU-board MainView's own `numberKeypress` (`main.js:92`) caught them
 * too — and its selector also covers `data-formatter_type="integer"`, which
 * the one below omits. `useBoardNumberInputs` restores that board binding and
 * deliberately skips only the inputs THIS hook matches, so the dialog's
 * integer-only boxes end up guarded exactly as legacy guarded them.
 *
 * `scopeSelector` exists because the header's Independent Rating popup mounts
 * the SAME selectors under a different root (`.component-selector-popup`), and
 * legacy covers it with this one binding: the header host is the same MainView
 * with `options.header = true` (component_selector.js:42-57 branches only on
 * which Dispatcher events it listens to — the `events` hash above it is
 * shared). Without a second mount there, every number field in the popup
 * accepted letters, Armenian included.
 *
 * The selector is legacy's verbatim, note `decimal` but not `integer`: an
 * integer field carrying no `data-type="number"` is unfiltered on dev too.
 *
 * The other four bindings in that hash (`numberFocusValidation`,
 * `numberKeydownValidation`'s arrow spinner, `numberKeyupValidation`'s
 * `.invalid` range class, `numberBlurValidation`'s comma reformat) all write
 * back to the DOM, and the selectors already do their own commit-time
 * formatting; they are not ported here.
 */
export function useRatingNumberKeypress(scopeSelector = RATING_DIALOG_SCOPE): void {
  useEffect(() => {
    const guarded = (e: Event): HTMLInputElement | null => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return null;
      if (!t.closest(scopeSelector)) return null;
      if (!t.matches(RATING_FIELD_SELECTOR)) return null;
      return t;
    };
    const onKeyPress = (e: KeyboardEvent) => {
      const t = guarded(e);
      if (t) numberKeypressFilter(e, t);
    };
    // Legacy has no second half — it predates the problem being noticed — but a
    // filter that only watches keystrokes leaves paste, drop and IME input as
    // ways in, and these boxes hold what the vendor APIs get called with. Same
    // rules as the keypress above by construction: both read the field off the
    // element.
    const onRepair = (e: Event) => {
      if (isLiveComposition(e)) return;
      const t = guarded(e);
      if (t && !t.readOnly) numberInputBackstop(t);
    };
    document.addEventListener('keypress', onKeyPress);
    document.addEventListener('input', onRepair);
    document.addEventListener('compositionend', onRepair);
    return () => {
      document.removeEventListener('keypress', onKeyPress);
      document.removeEventListener('input', onRepair);
      document.removeEventListener('compositionend', onRepair);
    };
  }, [scopeSelector]);
}
